import {useCallback, useState, useMemo} from 'react';
import {Popover, Box, InlineStack, Text, Icon} from '@shopify/polaris';
import {useToggle, useMediaLayout} from '@shopify/react-hooks';
import {useI18n} from '@shopify/react-i18n';
import {validLocalesForMarket} from 'utilities/helper';
import {
  type Market,
  useShopLocalesAndMarkets,
} from 'utilities/use-shop-locales-and-markets';
import {useUrlParams} from 'utilities/use-url-params';
import {
  BottomSheet,
  PlainAction,
  SelectionList,
} from '@shopify/online-store-ui';
import {ChevronLeftIcon, ChevronRightIcon} from '@shopify/polaris-icons';
import {MarketFlag} from 'components/MarketFlag';
import {FlagImagesProvider} from 'components/Flag';

import styles from './ContextSelector.scss';

interface Props {
  disabled: boolean;
  hideMarketSelector: boolean;
}

const scope = ['ContextSelector'];

export const ContextSelector = ({disabled, hideMarketSelector}: Props) => {
  const [i18n] = useI18n();
  const mobile = useMediaLayout('(max-width: 48rem)');
  const {
    value: contextSelectorVisible,
    toggle: toggleContextSelector,
    setFalse: hideContextSelector,
  } = useToggle(false);
  const {primaryLocale, currentLocale, currentMarket, locales, markets} =
    useShopLocalesAndMarkets();

  const allShopLocalesExceptPrimary = locales.filter(
    ({locale}) => locale !== primaryLocale.locale,
  );
  const {setUrlParams} = useUrlParams();

  const [selectedMarket, setSelectedMarket] = useState<Market | undefined>(
    currentMarket,
  );

  const handleClose = useCallback(() => {
    hideContextSelector();
    setSelectedMarket(undefined);
  }, [hideContextSelector]);

  const goBack = () => {
    setSelectedMarket(undefined);
  };

  const languageListItemAction = useCallback(
    (locale: string) => {
      setSelectedMarket(undefined);
      setUrlParams({shopLocale: locale, market: undefined});
      hideContextSelector();
    },
    [hideContextSelector, setUrlParams],
  );

  const marketListItemAction = useCallback(
    (marketHandle: string, locale: string) => {
      setUrlParams({shopLocale: locale, market: marketHandle});
      hideContextSelector();
    },
    [hideContextSelector, setUrlParams],
  );

  const goBackButton = (
    <Box
      paddingBlockEnd="200"
      paddingBlockStart={{sm: '0', md: '400'}}
      paddingInlineEnd="200"
      paddingInlineStart="200"
    >
      <InlineStack gap="100" blockAlign="center" wrap={false}>
        <PlainAction
          fontSize="bodyMd"
          slim
          noPadding
          icon={ChevronLeftIcon}
          onAction={goBack}
        />
        <Text as="span" variant="headingSm" tone="subdued" truncate>
          {
            i18n.translate(
              'adaptSpecificMarket',
              {scope},
              {
                marketName: selectedMarket?.name,
              },
            ) as unknown as string
          }
        </Text>
      </InlineStack>
    </Box>
  );

  const mainPage = (
    <SelectionList
      sections={[
        {
          title: i18n.translate('translateAcrossMarkets', {scope}),
          items: allShopLocalesExceptPrimary.map((locale) => ({
            content: locale.name,
            active: !currentMarket && currentLocale.locale === locale.locale,
            onAction: async () => {
              await languageListItemAction(locale.locale);
            },
          })),
        },
        {
          title: i18n.translate('adaptMarket', {scope}),
          items: markets.map((market) => ({
            content: market.name,
            onAction: () => {
              setSelectedMarket(market);
            },
            active: currentMarket?.id === market.id,
            prefix: <MarketFlag market={market} size="perfect-24" />,
            suffix: <Icon source={ChevronRightIcon} />,
            disabled: hideMarketSelector,
          })),
        },
      ]}
    />
  );

  const selectedMarketLocales = selectedMarket
    ? validLocalesForMarket(selectedMarket, markets)
    : [];

  const marketLocales = locales.filter((locale) => {
    return selectedMarketLocales.includes(locale.locale);
  });

  const localizationPage = (
    <>
      {goBackButton}
      <SelectionList
        sections={[
          {
            items: marketLocales.map((locale) => ({
              content: locale.name,
              active:
                currentLocale.locale === locale.locale &&
                currentMarket?.handle === selectedMarket?.handle,
              onAction: async () => {
                if (!selectedMarket) return;
                await marketListItemAction(
                  selectedMarket.handle,
                  locale.locale,
                );
              },
            })),
          },
        ]}
      />
    </>
  );

  const getCurrentPage = () => {
    if (selectedMarket) return localizationPage;
    return mainPage;
  };

  const content = useMemo(() => {
    if (currentMarket) {
      return i18n.translate(
        'contextualTitle.translateForMarket',
        {scope},
        {
          language: currentLocale.name,
          marketName: currentMarket?.name,
        },
      ) as unknown as string;
    }

    return i18n.translate(
      'contextualTitle.translateAllMarkets',
      {scope},
      {
        language: currentLocale.name,
      },
    ) as unknown as string;
  }, [currentMarket, currentLocale, i18n]);

  const plainActivator = (
    <PlainAction
      onAction={() => {
        toggleContextSelector();
        setSelectedMarket(currentMarket);
      }}
      prefix={
        currentMarket ? (
          <MarketFlag market={currentMarket} size="perfect-24" />
        ) : undefined
      }
      disclosure="chevronDown"
      content={mobile ? currentLocale.name : content}
      fontWeight="semibold"
      disabled={disabled}
      truncate
      slim={mobile}
    />
  );

  const popoverActivator = (
    <Box maxWidth="20rem">
      <Popover
        active={contextSelectorVisible}
        activator={plainActivator}
        onClose={handleClose}
        preferredAlignment="center"
      >
        <Box width="17rem" maxWidth="17rem">
          {getCurrentPage()}
        </Box>
      </Popover>
    </Box>
  );

  const bottomSheetMarkup = mobile && (
    <div className={styles.BottomSheet}>
      <BottomSheet
        expansion={!disabled && contextSelectorVisible ? 'full' : 'collapsed'}
        onExpansionChange={toggleContextSelector}
        titleTapExpansion="full"
        horizontalHeaderPadding="0"
        isModal
      >
        <BottomSheet.Title title={content} />
        {getCurrentPage()}
      </BottomSheet>
    </div>
  );

  const contextSelectorMarkup = mobile ? plainActivator : popoverActivator;

  return (
    <FlagImagesProvider>
      {contextSelectorMarkup}
      {bottomSheetMarkup}
    </FlagImagesProvider>
  );
};
